import { getNfcCardNoSchema } from "Components/Paypoints/schema";
import { UnstyledLink } from "Components/Routes/UnstyledLink";
import { CopyToClipboard } from "Components/Shared/CopyToClipboard";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { type AccessRoleCode, AccessRoleCodes } from "Models/AccessRoleCodes";
import { Resources, useResource } from "Translations/Resources";
import { isAuthenticated } from "Utils/AuthUtils";
import { appUrl } from "Utils/UrlUtils";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Box, Typography } from "@mui/material";

const COMPONENT_RESOURCES = Resources.Nfc;

const ROLES_WITH_PAYPOINTS_ACCESS: Array<AccessRoleCode> = [
  AccessRoleCodes.Merchant,
  AccessRoleCodes.Hunter,
  AccessRoleCodes.Admin,
];

type Props = {
  nfcCardNo?: string;
};

export const IdleNfcInfo = ({ nfcCardNo }: Props) => {
  const user = useLoggedInUser();
  const { t } = useResource();
  const isUserAuthenticated = isAuthenticated(user);
  const isUserWithPaypointsAccess = user?.accessRoleCode
    ? ROLES_WITH_PAYPOINTS_ACCESS.includes(
        user.accessRoleCode as AccessRoleCode,
      )
    : false;
  const canManipulatePaypoints =
    isUserAuthenticated && isUserWithPaypointsAccess;
  const isNfcCardNoValid =
    nfcCardNo && getNfcCardNoSchema(t).safeParse(nfcCardNo).success;

  return (
    <PrimaryPaper
      sx={{
        width: "fit-content",
        ...(isNfcCardNoValid
          ? {}
          : {
              border: "1px solid",
              borderColor: "error.main",
            }),
      }}
    >
      {!isNfcCardNoValid && (
        <Box display="flex" alignItems="center" gap={2} mb={4}>
          <WarningAmberOutlinedIcon
            sx={({ palette }) => ({
              color: palette.error.main,
              width: 20,
              height: 20,
            })}
          />
          <ErrorText fontSize="1rem" fontWeight={500}>
            {t(COMPONENT_RESOURCES.InvalidNfcCardNoFormat)}
          </ErrorText>
        </Box>
      )}
      <Typography variant="h6">{t(COMPONENT_RESOURCES.NfcCardNo)}</Typography>
      <Box display="flex" alignItems="center" gap={2}>
        <Typography
          variant="h4"
          color={isNfcCardNoValid ? "text.primary" : "error.main"}
        >
          {nfcCardNo}
        </Typography>
        {nfcCardNo && (
          <CopyToClipboard
            text={nfcCardNo}
            accessibilityLabel={t(COMPONENT_RESOURCES.CopyNfcCardNo)}
            successMessage={t(COMPONENT_RESOURCES.CopyNfcCardNoSuccess)}
          />
        )}
      </Box>

      <Box mt={10}>
        <Typography>{t(COMPONENT_RESOURCES.CardNotAssigned)}</Typography>
      </Box>
      {isNfcCardNoValid && canManipulatePaypoints && (
        <Typography variant="h6" mt={2} textAlign="center">
          <UnstyledLink
            to={`${appUrl("nfc-assign")}/${nfcCardNo}/assign`}
            isWithIcon
          >
            {t(COMPONENT_RESOURCES.AssignToPaypoint)}
          </UnstyledLink>
        </Typography>
      )}
    </PrimaryPaper>
  );
};
